import Plan from "components/UI/Plan"
import { navigate } from "gatsby"
import React, { useEffect, useMemo, useState } from "react"
import styled from "styled-components"
import CurrencySelector from "./CurrencySelector"
import { useQuarterlyUpsellModal } from "./QuarterlyUpsellModal"
import { priceToPlanProps } from "./utils"

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  z-index: 1;
  * {
    box-sizing: border-box;
  }
  > .background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    object-fit: cover;
  }
  align-items: center;
  width: 100%;
  h1 {
    font-weight: normal;
    font-size: 2em;
    font-family: "Rammetto One", cursive;
    text-shadow: 0 3px 0 #afafaf, 0 5px 0 rgba(0, 0, 0, 0.3);
  }

  > .select-currency {
    width: auto;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 1em;
    font-family: "Libre Baskerville";
  }
`

export type PlansSelectorPrice = {
  id: string
  nickname: string
  currency: string
  recurring: {
    interval: string
    interval_count: number
  }
  billing_scheme: string
  tiers: {
    flat_amount: number | null
    unit_amount: number | null
    up_to: number | null
  }[]
}

type Props = {
  prices: PlansSelectorPrice[]
}

const PlansList = styled.div`
  padding: 25px 0px 40px 0;
  display: flex;
  gap: 50px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
`

export const PlansSelector = (props: Props) => {
  const prices = props.prices
  const quarterlyUpsellModal = useQuarterlyUpsellModal()

  const currencies = useMemo(() => {
    const s = prices.reduce((acc, p) => {
      acc.add(p.currency)
      return acc
    }, new Set<string>())
    return Array.from(s)
  }, [prices])

  const [currency, setCurrency] = useState<string | null>()
  useEffect(() => {
    let defaultCurrency = currencies[0]
    if (currencies.indexOf("usd") >= 0) defaultCurrency = "usd"
    setCurrency(defaultCurrency)
  }, [currencies])

  const filteredPrices = useMemo(() => {
    return prices.filter(p => p.tiers && p.currency === currency)
  }, [currency, prices])

  return (
    <Wrapper className="plans-selector">
      {currencies.length > 1 && (
        <div className="select-currency">
          <CurrencySelector
            currencies={currencies}
            currency={currency}
            onChange={c => {
              setCurrency(c)
            }}
          />
        </div>
      )}
      <PlansList>
        {filteredPrices.map(p => (
          <Plan
            key={p.id}
            {...priceToPlanProps(p)}
            onClick={() => {
              if (p.recurring.interval === 'month' && p.recurring.interval_count === 1) {
                const monthly = p
                const quarterly = filteredPrices.find(p => p.recurring.interval === 'month' && p.recurring.interval_count === 3)
                if (!quarterly) {
                  navigate(`/parents/checkout/${p.id}`)
                  return
                }
                quarterlyUpsellModal.open({
                  monthly,
                  quarterly
                })
              } else {
                navigate(`/parents/checkout/${p.id}`)
              }
            }}
            linkTo={`/parents/checkout/${p.id}`}
          />
        ))}
      </PlansList>
      {quarterlyUpsellModal.Modal}

      {currency === 'cad' && <div style={{
        color: '#fff',
        fontStyle: 'italic',
        fontSize: '0.8em',
        marginTop: '20px'
      }}>*Please note that the prices above <b>don't include</b> any Canadian sales tax.</div>}
    </Wrapper>

  )
}
